/**
 * Resource-node resolve chain (E3.4.S3) — turn a join-only `NodeWire` into the
 * asset-pack + model the placer instantiates at the node's (x, z). This is the
 * AD-141 / DR-020 node→asset seam, closed end-to-end for the first time: before
 * this module a resource node reached the client on the join ack and was drawn
 * ONLY as a HUD minimap dot (`ui/Hud.tsx`) — nothing put it in the world.
 *
 *   NodeWire.resource                                  (a resource CONTENT id)
 *     → client-pack resources[id].gatheringSkill        (shipped: PRESENTATION_FIELDS)
 *       → RESOURCE_NODE_ASSET[skill]                    (shared/content/types.ts)
 *         → the archetype id (`node-flora` | `node-mineral` | `node-herb` | `node-salvage`)
 *           → the DELIVERED asset pack that carries that archetype
 *             → instantiate(archetype, lod0)            (the placer's job)
 *
 * **Why the last hop is a search and not a lookup, which is the whole design
 * problem here.** The biome's `generation.nodeKit` names the kit — but `nodeKit`
 * is on the client-pack SECRET list (`shared/content/client-pack.ts`: the
 * `generation` sub-projection ships scatter + settlements and NOTHING else, and
 * `client-pack.test.ts` asserts `nodeKit` is stripped). The client therefore
 * cannot be TOLD which kit to use, so it reaches the archetype through the packs
 * it was actually given. That is exactly the frontend-local-convention pattern
 * `creatures/resolve.ts` uses for enemies (which have no contentId on the wire),
 * and it is a client convention — never a `shared/` constant, never a validator
 * semantic. Promoting any of it is a T3 DR escalation out of the feature.
 *
 * **Ambiguity is a DETERMINISTIC no-binding, never an arbitrary winner.** Exactly
 * one delivered pack carries the archetypes today (`resource-node-kit-v1`, bound
 * by both `the-verdant-reach` and `ashenveil-depths`), but a second kit is a
 * content act, not a code change. Two packs offering the same archetype ⇒ `null`,
 * so the placer degrades to its placeholder rather than picking whichever pack
 * happened to sort first. `hunting` deliberately has no archetype (its yields drop
 * from enemies, not world nodes — `resource-node-convention.test.ts`), so a
 * hunting resource resolves to `null` by the same door.
 *
 * Pure + node-safe (types only from the client surface). Import boundary
 * (AD-122): shared types via `@great-cloud/shared/client`.
 */

import { RESOURCE_NODE_ASSET, type AssetPackDef } from '@great-cloud/shared/client';

/**
 * The projected resource fields the placer reads (client-pack `resources`, which
 * ships BASE_FIELDS + tier/`gatheringSkill`/qualityRange/stackable). Only
 * `gatheringSkill` drives resolution; the node's id/x/z come from the `NodeWire`.
 */
export interface ResourcePresentation {
  /** Which gathering skill harvests this resource — the archetype key. */
  gatheringSkill?: string;
}

/**
 * How the placer reads node presentation data — the client content pack's
 * `resources` and `asset-packs` projections. Built in App from the loaded
 * `ContentPackStore`; a small test fake satisfies it too. `assetPacks()` is a LIVE
 * read because the pack loads after `Game` constructs. Kept separate from
 * `NpcContent` / `CreatureContent` / `SceneContentLookup` so this feature touches
 * no other module's contract.
 */
export interface NodeContent {
  /** Client-pack `resources[id]`, or undefined if the resource was not delivered. */
  resource(id: string): ResourcePresentation | undefined;
  /** Every delivered asset pack (the client-pack `asset-packs` category values). */
  assetPacks(): readonly AssetPackDef[];
}

/** A resolved node render: the pack + archetype the placer instantiates. */
export interface NodeResolution {
  /** The resolved asset-pack id (the kit that carries the archetype). */
  assetPack: string;
  /** The archetype asset id within that pack, instantiated at lod0. */
  model: string;
  /** The resolved pack def — an `AssetManifest`/`AssetLoader` is built from it. */
  pack: AssetPackDef;
  /** The archetype the resource mapped to — recorded so tests can name the hop. */
  archetype: string;
}

/**
 * The archetype a resource's node should render as, or `null` when the resource is
 * undelivered, carries no gathering skill, or its skill has no node archetype
 * (`hunting`). Exported because it is the half of the chain that is pure content
 * convention — the pack search below is the half that depends on delivery.
 */
export function nodeArchetypeFor(content: NodeContent, resourceId: string): string | null {
  const skill = content.resource(resourceId)?.gatheringSkill;
  if (skill === undefined || skill === '') return null;
  const archetype = (RESOURCE_NODE_ASSET as Record<string, string | undefined>)[skill];
  return archetype ?? null;
}

/**
 * Resolve one node's render, or `null` when nothing can be resolved — the placer
 * then falls back to its placeholder, which is the graceful-degradation contract
 * (WR.3.2) and, for nodes, still strictly more than the nothing they rendered
 * before. The final `instantiate(model, lod0)` — and its own null path for an
 * unknown model or a load error — is the placer's/loader's job.
 */
export function resolveNode(content: NodeContent, resourceId: string): NodeResolution | null {
  const archetype = nodeArchetypeFor(content, resourceId);
  if (archetype === null) return null;
  let found: AssetPackDef | null = null;
  for (const pack of content.assetPacks()) {
    if (!pack.assets?.some((asset) => asset.id === archetype)) continue;
    if (found !== null) return null; // ≥2 delivered kits offer it ⇒ deterministic no-binding
    found = pack;
  }
  if (found === null) return null; // no delivered pack carries the archetype
  return { assetPack: found.id, model: archetype, pack: found, archetype };
}
